/*
  Wysuwany koszyk (Dawn <cart-drawer>) — LALE DRAFT 8, 16.10.2026 ("koszyk").

  Zachowane z Dawn (inny kod w motywie na tym polega):
    - klasy CartDrawer i CartDrawerItems, API: open(triggeredBy), close(), renderContents(parsedState),
      getSectionsToRender(), setActiveElement(), activeElement; PDP (snippets/product-configurator.liquid) wola
      getSectionsToRender() + renderContents(); cart.js podmienia .drawer__inner i #cart-icon-bubble przy zmianie ilosci.
    - klasa .active na <cart-drawer> (pasek zakupu na karcie produktu chowa sie, gdy koszyk jest otwarty).
  Nowe:
    - otwieranie z ikony koszyka w naglowku przez delegacje zdarzen (dziala tez, gdy naglowek zostanie podmieniony);
      bez JS ikona zostaje zwyklym linkiem do /cart; Ctrl/Cmd/Shift-klik i srodkowy przycisk otwieraja /cart jak zwykle;
      na samej stronie /cart ikona nie otwiera szuflady (klient juz jest w koszyku);
    - zamykanie: przycisk X, klik w nakladke, Esc (takze gdy ostrosc uciekla poza panel); Esc w otwartym polu uwag zamyka tylko pole;
    - pulapka ostrosci: Tab/Shift+Tab krazy w panelu (gdy ostrosc zgubiona, Tab wraca do panelu); reszta strony (#MainContent,
      naglowek i stopka) dostaje inert, wiec czytnik ekranu i klawiatura nie wychodza poza okno; cudze nakladki (okno Shop Pay /
      Google Pay, baner cookies) nie sa ruszane; po zamknieciu ostrosc wraca na wywolujacy element;
    - blokada przewijania tla (body.overflow-hidden + html.lale-cd-open), aria-expanded na ikonie koszyka;
    - renderContents bez sekcji w odpowiedzi (np. dodanie bez parametru sections) dociaga szuflade przez Section Rendering API.
*/
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.inertTargets = [];

    // Ostatni klikniety przycisk/link: karta produktu wylacza przycisk Add to cart na czas dodawania (ostrosc ucieka na <body>),
    // a po zamknieciu koszyka ostrosc ma wrocic wlasnie tam.
    document.addEventListener(
      'click',
      (event) => {
        const el = event.target instanceof Element ? event.target.closest('button, a[href], [role="button"]') : null;
        if (el && !this.contains(el)) this.lastClick = { el, at: Date.now() };
      },
      true
    );

    // Delegacja: przyciski zamykania i nakladka sa odtwarzane przy kazdym odswiezeniu szuflady (innerHTML).
    this.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-lale-cd-close]')) {
        event.preventDefault();
        this.close();
        return;
      }
      if (target.id === 'CartDrawer-Overlay') this.close();
    });

    this.setHeaderCartIconAccessibility();
  }

  isCartPage() {
    return /\/cart\/?$/.test(window.location.pathname);
  }

  setHeaderCartIconAccessibility() {
    const markTriggers = () => {
      document.querySelectorAll('#cart-icon-bubble, [data-lale-cart-drawer-trigger]').forEach((el) => {
        if (this.isCartPage()) return;
        if (el.getAttribute('role') !== 'button') el.setAttribute('role', 'button');
        el.setAttribute('aria-haspopup', 'dialog');
        if (!el.hasAttribute('aria-expanded')) el.setAttribute('aria-expanded', 'false');
      });
    };
    markTriggers();

    document.addEventListener('click', (event) => {
      if (this.isCartPage()) return;
      const trigger = event.target instanceof Element && event.target.closest('#cart-icon-bubble, [data-lale-cart-drawer-trigger]');
      if (!trigger) return;
      // Ctrl/Cmd/Shift/Alt-klik i inne przyciski niz lewy: zwykle przejscie do /cart
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.open(trigger);
    });

    document.addEventListener('keydown', (event) => {
      if (this.isCartPage()) return;
      const trigger = event.target instanceof Element && event.target.closest('#cart-icon-bubble, [data-lale-cart-drawer-trigger]');
      if (!trigger || event.code.toUpperCase() !== 'SPACE') return;
      event.preventDefault();
      this.open(trigger);
    });
  }

  getFocusables() {
    const container = this.querySelector('.drawer__inner');
    if (!container) return [];
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.closest('[hidden]') && !el.closest('[inert]') && el.getClientRects().length > 0 && el.getAttribute('tabindex') !== '-1');
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    } else if (!this.classList.contains('active')) {
      // dodanie do koszyka z karty produktu: zapamietaj przycisk, z ktorego przyszlismy (ostrosc wroci tam po zamknieciu)
      const current = document.activeElement;
      if (current && current !== document.body && current !== document.documentElement && !this.contains(current)) {
        this.setActiveElement(current);
      } else if (this.lastClick && Date.now() - this.lastClick.at < 8000 && document.contains(this.lastClick.el)) {
        this.setActiveElement(this.lastClick.el);
      }
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    const wasActive = this.classList.contains('active');
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    document.body.classList.add('overflow-hidden');
    document.documentElement.classList.add('lale-cd-open');
    this.setTriggersExpanded(true);
    this.setPageInert(true);

    document.removeEventListener('keydown', this.onDocumentKeydown, true);
    document.addEventListener('keydown', this.onDocumentKeydown, true);

    // ostrosc na panelu (czytnik ekranu odczyta nazwe okna) po zakonczeniu wsuwania; zapas czasowy, gdy ruch jest wylaczony
    const moveFocus = () => {
      if (!this.classList.contains('active')) return;
      const inner = this.querySelector('.drawer__inner');
      if (!inner) return;
      if (!inner.contains(document.activeElement) || document.activeElement === inner) inner.focus({ preventScroll: true });
    };
    if (wasActive) {
      setTimeout(moveFocus, 30);
    } else {
      const inner = this.querySelector('.drawer__inner');
      let done = false;
      const once = () => {
        if (done) return;
        done = true;
        if (inner) inner.removeEventListener('transitionend', onEnd);
        moveFocus();
      };
      const onEnd = (event) => {
        if (event.target === inner && event.propertyName === 'transform') once();
      };
      if (inner) inner.addEventListener('transitionend', onEnd);
      setTimeout(once, 480);
    }
  }

  close() {
    if (!this.classList.contains('active') && !document.body.classList.contains('overflow-hidden')) return;
    this.classList.remove('active');
    document.removeEventListener('keydown', this.onDocumentKeydown, true);
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('lale-cd-open');
    this.setPageInert(false);
    this.setTriggersExpanded(false);

    // ostrosc wraca na element, ktory otworzyl koszyk (a gdy go juz nie ma: na ikone koszyka w naglowku)
    let target = this.activeElement;
    if (!target || !document.contains(target) || target.disabled || target.closest('[inert]')) target = document.querySelector('#cart-icon-bubble');
    removeTrapFocus(target && typeof target.focus === 'function' ? target : null);
  }

  setTriggersExpanded(expanded) {
    document.querySelectorAll('#cart-icon-bubble, [data-lale-cart-drawer-trigger]').forEach((el) => {
      if (el.hasAttribute('aria-haspopup')) el.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  // Reszta strony nie bierze udzialu, gdy okno jest otwarte (aria-modal + inert); wylacznie atrybut, bez zmiany ukladu.
  setPageInert(on) {
    if (!('inert' in HTMLElement.prototype)) return;
    if (on) {
      if (this.inertTargets.length) return;
      this.inertTargets = Array.from(document.querySelectorAll('#MainContent, [id^="shopify-section-group-"]')).filter(
        (el) => !el.contains(this) && !this.contains(el)
      );
      this.inertTargets.forEach((el) => {
        el.inert = true;
      });
    } else {
      this.inertTargets.forEach((el) => {
        el.inert = false;
      });
      this.inertTargets = [];
    }
  }

  onDocumentKeydown(event) {
    if (!this.classList.contains('active')) return;
    const key = event.key;

    // Ostrosc w cudzej nakladce (okno Shop Pay / Google Pay, baner cookies) zostaje w spokoju: ani Esc, ani Tab stad nie ruszaja szuflady.
    const target = event.target;
    const foreign =
      target instanceof Element && target !== document.body && target !== document.documentElement && !this.contains(target);
    if (foreign) return;

    if (key === 'Escape' || key === 'Esc') {
      // Esc w otwartym polu uwag zamyka samo pole (obsluga w global.js: onKeyUpEscape), nie cala szuflade
      const openDetails = event.target instanceof Element && event.target.closest('details[open]');
      if (openDetails && this.contains(openDetails)) return;
      // Esc w oknie informacji o ilosciach zamyka to okno (quantity-popover.js)
      const openInfo = event.target instanceof Element && event.target.closest('.quantity-popover__info:not([hidden])');
      if (openInfo) return;
      event.preventDefault();
      this.close();
      return;
    }

    if (key !== 'Tab') return;
    const focusables = this.getFocusables();
    const inner = this.querySelector('.drawer__inner');
    if (!inner) return;
    if (!focusables.length) {
      event.preventDefault();
      inner.focus({ preventScroll: true });
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    const inside = inner.contains(active);
    if (event.shiftKey && (active === first || active === inner || !inside)) {
      event.preventDefault();
      event.stopPropagation();
      last.focus();
    } else if (!event.shiftKey && (active === last || !inside)) {
      event.preventDefault();
      event.stopPropagation();
      first.focus();
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', cartDrawerNote.closest('details').hasAttribute('open') ? 'true' : 'false');

    if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Dawn zdejmowal is-empty tylko z .drawer__inner; stan pusty/pelny wynika teraz z samej tresci, ale klase czyscimy tu tez
    this.classList.remove('is-empty');
    this.productId = parsedState && parsedState.id;

    const sections = parsedState && parsedState.sections;
    const apply = (htmlBySection) => {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement || !htmlBySection[section.id]) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(htmlBySection[section.id], section.selector);
      });
      setTimeout(() => {
        this.open();
      });
    };

    if (sections && sections['cart-drawer']) {
      apply(sections);
      return;
    }

    // brak sekcji w odpowiedzi: dociagnij szuflade i ikone z Section Rendering API
    const base = (window.routes && window.routes.cart_url) || '/cart';
    Promise.all(
      ['cart-drawer', 'cart-icon-bubble'].map((id) =>
        fetch(`${base}?section_id=${id}`)
          .then((response) => response.text())
          .then((html) => [id, html])
      )
    )
      .then((pairs) => apply(Object.fromEntries(pairs)))
      .catch((e) => console.error(e));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
